import React, { useEffect, useState } from 'react';
import { 
    Container, Typography, Button, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Paper, IconButton, 
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Box
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { configService } from '../services/config';

const Security = () => {
    const [configs, setConfigs] = useState([]);
    const [open, setOpen] = useState(false);
    const [editingConfig, setEditingConfig] = useState(null);
    const [formData, setFormData] = useState({
        provider: '',
        logo: '',
        credentials: { id: '', secret: '' }
    });

    useEffect(() => {
        loadConfigs();
    }, []);

    const loadConfigs = async () => {
        try {
            const result = await configService.getAll();
            setConfigs(result.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    const handleOpen = (config = null) => {
        if (config) {
            setEditingConfig(config);
            setFormData({
                provider: config.provider,
                logo: config.logo || '',
                credentials: { id: config.credentials.id, secret: config.credentials.secret }
            });
        } else {
            setEditingConfig(null);
            setFormData({
                provider: '',
                logo: '',
                credentials: { id: '', secret: '' }
            });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSubmit = async () => {
        try {
            if (editingConfig) {
                await configService.update(editingConfig._id, formData);
            } else {
                await configService.create(formData);
            }
            loadConfigs();
            handleClose();
        } catch (error) {
            console.error(error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this provider configuration?')) {
            try {
                await configService.delete(id);
                loadConfigs();
            } catch (error) {
                console.error(error);
            }
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">Social Provider Configurations</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
                    Add Provider
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Provider</TableCell>
                            <TableCell>Logo</TableCell>
                            <TableCell>Client ID</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {configs.map((config) => (
                            <TableRow key={config._id}>
                                <TableCell sx={{ textTransform: 'capitalize' }}>{config.provider}</TableCell>
                                <TableCell>
                                    {config.logo && (
                                        <img src={config.logo} alt={config.provider} style={{ height: 24 }} />
                                    )}
                                </TableCell>
                                <TableCell><code>{config.credentials.id}</code></TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleOpen(config)} color="primary">
                                        <Edit />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(config._id)} color="error">
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editingConfig ? 'Edit Provider' : 'New Provider'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Provider Name (e.g., google, github)"
                        fullWidth
                        variant="outlined"
                        value={formData.provider}
                        onChange={(e) => setFormData({ ...formData, provider: e.target.value.toLowerCase() })}
                        disabled={!!editingConfig}
                        sx={{ mb: 2, mt: 1 }}
                    />
                    <TextField
                        margin="dense"
                        label="Logo URL"
                        fullWidth
                        variant="outlined"
                        value={formData.logo}
                        onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Client ID"
                        fullWidth
                        variant="outlined"
                        value={formData.credentials.id}
                        onChange={(e) => setFormData({ 
                            ...formData, 
                            credentials: { ...formData.credentials, id: e.target.value } 
                        })}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Client Secret"
                        fullWidth
                        variant="outlined"
                        type="password"
                        value={formData.credentials.secret}
                        onChange={(e) => setFormData({ 
                            ...formData, 
                            credentials: { ...formData.credentials, secret: e.target.value } 
                        })}
                        sx={{ mb: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {editingConfig ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Security;
